(() => {
  const rootPath = window.Shopify?.routes?.root || "/";
  let currentCart = null;

  function cartUrl(path) {
    return `${rootPath}${path}`.replace(/\/{2,}/g, "/");
  }

  async function cartRequest(path, options = {}) {
    const response = await fetch(cartUrl(path), {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  async function getCart() {
    currentCart = await cartRequest("cart.js");
    updateCartHeader(currentCart);
    return currentCart;
  }

  function updateCartHeader(cart) {
    document.querySelectorAll("[data-frml-cart-count]").forEach((node) => {
      node.textContent = String(cart.item_count || 0);
    });
    document.querySelectorAll("[data-frml-checkout]").forEach((node) => {
      node.hidden = !cart.item_count;
    });
  }

  function beginCartLinesEvent(target, { action, context, lines }) {
    const StandardEvents = window.Shopify?.StandardEvents;
    if (!StandardEvents?.CartLinesUpdateEvent) return null;

    const deferred = StandardEvents.CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {
      // A rejected cart operation is also reported through CartErrorEvent.
    });
    target.dispatchEvent(
      new StandardEvents.CartLinesUpdateEvent({
        action,
        context,
        lines,
        promise: deferred.promise,
      }),
    );

    return { StandardEvents, deferred, target };
  }

  function resolveCartLinesEvent(cartEvent, cart) {
    if (!cartEvent) return;
    cartEvent.deferred.resolve({
      cart: cartEvent.StandardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(
        cart,
      ),
    });
  }

  function rejectCartLinesEvent(cartEvent, error) {
    if (!cartEvent) return;
    cartEvent.target.dispatchEvent(
      new cartEvent.StandardEvents.CartErrorEvent({
        error: error instanceof Error ? error.message : String(error),
        code: "SERVICE_UNAVAILABLE",
      }),
    );
    cartEvent.deferred.reject(error);
  }

  function initializeShopMedia(root = document) {
    root.querySelectorAll("[data-frml-shop-media]").forEach((container) => {
      if (container.dataset.frmlReady === "true") return;
      container.dataset.frmlReady = "true";

      const images = Array.from(
        container.querySelectorAll("[data-frml-shop-image]"),
      );
      const count = container.querySelector("[data-frml-shop-index]");
      if (images.length < 2) return;
      let activeIndex = 0;
      let preloaded = false;

      const show = (nextIndex) => {
        const bounded = Math.max(0, Math.min(images.length - 1, nextIndex));
        if (bounded === activeIndex && !images[bounded].hidden) return;
        images.forEach((image, index) => {
          const active = index === bounded;
          image.hidden = !active;
          image.classList.toggle("is-active", active);
        });
        activeIndex = bounded;
        if (count) count.textContent = String(bounded + 1);
      };

      const preload = () => {
        if (preloaded) return;
        preloaded = true;
        images.slice(1).forEach((image) => {
          const loader = new Image();
          loader.sizes = image.sizes;
          loader.srcset = image.srcset;
          loader.src = image.currentSrc || image.src;
        });
      };

      container.addEventListener("focus", preload);
      container.addEventListener("pointerenter", preload);
      container.addEventListener("pointerleave", () => show(0));
      container.addEventListener("pointermove", (event) => {
        if (event.pointerType !== "mouse") return;
        const bounds = container.getBoundingClientRect();
        const progress = Math.max(
          0,
          Math.min(0.999, (event.clientX - bounds.left) / bounds.width),
        );
        show(Math.floor(progress * images.length));
      });
      container.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        show((activeIndex + direction + images.length) % images.length);
      });
    });
  }

  function initializeProductGallery(root = document) {
    root.querySelectorAll("[data-frml-product-gallery]").forEach((gallery) => {
      if (gallery.dataset.frmlReady === "true") return;
      gallery.dataset.frmlReady = "true";

      const image = gallery.querySelector("[data-frml-product-image]");
      const count = gallery.querySelector("[data-frml-product-index]");
      const thumbnails = Array.from(
        gallery.querySelectorAll("[data-frml-thumbnail]"),
      );
      if (!image || !thumbnails.length) return;

      const activate = (thumbnail) => {
        thumbnails.forEach((candidate) => {
          candidate.setAttribute(
            "aria-pressed",
            candidate === thumbnail ? "true" : "false",
          );
        });

        image.classList.remove("product-inspector-image");
        image.src = thumbnail.dataset.imageSrc;
        image.srcset = thumbnail.dataset.imageSrcset || "";
        image.alt = thumbnail.dataset.imageAlt || "";
        image.classList.toggle(
          "is-transparent",
          thumbnail.dataset.imageKind === "transparent",
        );
        void image.offsetWidth;
        image.classList.add("product-inspector-image");
        if (count) {
          count.textContent = String(
            Number(thumbnail.dataset.imageIndex || 0) + 1,
          );
        }
      };

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", () => activate(thumbnail));
        thumbnail.addEventListener("pointerenter", () => {
          const loader = new Image();
          loader.srcset = thumbnail.dataset.imageSrcset || "";
          loader.src = thumbnail.dataset.imageSrc;
        });
      });

      gallery.frmlSelectColor = (color) => {
        const matching = thumbnails.find(
          (thumbnail) =>
            thumbnail.dataset.imageColor?.toLowerCase() === color.toLowerCase(),
        );
        if (matching) activate(matching);
      };
    });
  }

  function normalizeProductDescriptions(root = document) {
    root.querySelectorAll("[data-frml-product-description]").forEach((copy) => {
      if (copy.dataset.frmlReady === "true") return;
      copy.dataset.frmlReady = "true";
      copy.querySelectorAll("meta").forEach((node) => node.remove());
      copy.querySelectorAll("br").forEach((node) => {
        node.replaceWith(document.createTextNode(" "));
      });
      copy.querySelectorAll("p, li").forEach((node) => {
        const original = node.textContent.replace(/\u00a0/g, " ").trim();
        if (!original || /\bpremade\b|ships immediately/i.test(original)) {
          node.remove();
          return;
        }
        const trailingMarks = original.match(/["')\]]+$/)?.[0] || "";
        const sentence = trailingMarks
          ? original.slice(0, -trailingMarks.length)
          : original;
        node.textContent = /[.!?…]$/.test(sentence)
          ? original
          : `${sentence}.${trailingMarks}`;
      });
      copy.querySelectorAll("ul, ol").forEach((list) => {
        if (!list.children.length) list.remove();
      });
    });
  }

  function formatVariantPrice(cents) {
    const currency = window.Shopify?.currency?.active || "USD";
    const amount = Number(cents) / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }

  function initializeProductForms(root = document) {
    root.querySelectorAll(".product-order-form").forEach((form) => {
      if (form.dataset.frmlReady === "true") return;
      form.dataset.frmlReady = "true";

      const variantsNode = form.querySelector("[data-frml-product-variants]");
      const cartStateNode = form.querySelector("[data-frml-cart-state]");
      const variantInput = form.querySelector("[data-frml-variant-id]");
      const addButton = form.querySelector("[data-frml-add]");
      const removeButton = form.querySelector("[data-frml-remove]");
      const actions = form.querySelector("[data-frml-cart-actions]");
      const status = form.querySelector("[data-frml-cart-status]");
      const productRoot = form.closest("[data-frml-product]");
      const gallery = productRoot?.querySelector("[data-frml-product-gallery]");
      const price = productRoot?.querySelector("[data-frml-product-price]");
      const fields = Array.from(form.querySelectorAll("[data-option-position]"));
      if (
        !variantsNode ||
        !variantInput ||
        !addButton ||
        !removeButton ||
        !actions
      ) {
        return;
      }

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch {
        if (status) status.textContent = "unavailable";
        addButton.disabled = true;
        return;
      }

      if (currentCart === null && cartStateNode) {
        try {
          currentCart = JSON.parse(cartStateNode.textContent);
        } catch {
          currentCart = { items: [] };
        }
      }

      let selectedOptions = [];
      fields.forEach((field) => {
        const position = Number(field.dataset.optionPosition);
        const selected = field.querySelector(
          '[data-frml-option-value][aria-pressed="true"]',
        );
        if (selected) selectedOptions[position] = selected.dataset.frmlOptionValue;
      });

      let selectedVariant =
        variants.find(
          (variant) => String(variant.id) === String(variantInput.value),
        ) || variants.find((variant) => variant.available);

      if (selectedVariant) selectedOptions = [...selectedVariant.options];

      const lineForVariant = (cart, variant) =>
        cart?.items?.find(
          (item) => String(item.variant_id) === String(variant?.id),
        );

      const updateRemoveState = () => {
        const inCart = Boolean(lineForVariant(currentCart, selectedVariant));
        removeButton.hidden = !inCart;
        actions.classList.toggle("has-remove", inCart);
      };

      const sync = () => {
        if (!selectedVariant) {
          addButton.disabled = true;
          if (status) status.textContent = "unavailable";
          return;
        }

        variantInput.value = selectedVariant.id;
        addButton.disabled = !selectedVariant.available;
        fields.forEach((field) => {
          const position = Number(field.dataset.optionPosition);
          field.querySelectorAll("[data-frml-option-value]").forEach((button) => {
            const active =
              button.dataset.frmlOptionValue === selectedOptions[position];
            button.classList.toggle("is-selected", active);
            button.setAttribute("aria-pressed", active ? "true" : "false");
          });
        });
        if (price) price.textContent = formatVariantPrice(selectedVariant.price);
        if (status) status.textContent = "";
        updateRemoveState();
      };

      fields.forEach((field) => {
        const position = Number(field.dataset.optionPosition);
        field.querySelectorAll("[data-frml-option-value]").forEach((button) => {
          button.addEventListener("click", () => {
            const value = button.dataset.frmlOptionValue;
            const nextOptions = [...selectedOptions];
            nextOptions[position] = value;

            let nextVariant = variants.find(
              (variant) =>
                variant.available &&
                variant.options.every(
                  (option, index) => option === nextOptions[index],
                ),
            );

            if (!nextVariant) {
              nextVariant = variants.find(
                (variant) =>
                  variant.available && variant.options[position] === value,
              );
            }
            if (!nextVariant) return;

            selectedVariant = nextVariant;
            selectedOptions = [...nextVariant.options];
            addButton.textContent = "add to cart";
            if (
              field.querySelector("legend")?.textContent.trim().toLowerCase() ===
              "color"
            ) {
              gallery?.frmlSelectColor?.(value);
            }
            sync();
          });
        });
      });

      const setPending = (pending) => {
        addButton.disabled = pending || !selectedVariant?.available;
        removeButton.disabled = pending;
      };

      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!selectedVariant?.available) return;
        setPending(true);
        addButton.textContent = "working…";
        if (status) status.textContent = "";
        const cartEvent = beginCartLinesEvent(productRoot || form, {
          action: "add",
          context: "product",
          lines: [{ merchandiseId: selectedVariant.id, quantity: 1 }],
        });

        try {
          await cartRequest("cart/add.js", {
            method: "POST",
            body: JSON.stringify({
              items: [{ id: selectedVariant.id, quantity: 1 }],
            }),
          });
          const cart = await getCart();
          resolveCartLinesEvent(cartEvent, cart);
          addButton.textContent = "added";
          updateRemoveState();
        } catch (error) {
          rejectCartLinesEvent(cartEvent, error);
          addButton.textContent = "add to cart";
          if (status) status.textContent = "Could not add that shirt. Try again.";
        } finally {
          setPending(false);
        }
      });

      removeButton.addEventListener("click", async () => {
        const line = lineForVariant(currentCart, selectedVariant);
        if (!line) return;
        setPending(true);
        if (status) status.textContent = "";
        const cartEvent = beginCartLinesEvent(productRoot || form, {
          action: "remove",
          context: "product",
          lines: [{ id: line.key, quantity: 0 }],
        });

        try {
          currentCart = await cartRequest("cart/change.js", {
            method: "POST",
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          });
          updateCartHeader(currentCart);
          resolveCartLinesEvent(cartEvent, currentCart);
          addButton.textContent = "add to cart";
          updateRemoveState();
        } catch (error) {
          rejectCartLinesEvent(cartEvent, error);
          if (status) {
            status.textContent = "Could not remove that shirt. Try again.";
          }
        } finally {
          setPending(false);
        }
      });

      sync();
    });
  }

  function initialize(root = document) {
    initializeShopMedia(root);
    initializeProductGallery(root);
    normalizeProductDescriptions(root);
    initializeProductForms(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();
